import axios from "axios";
import { useState, useContext, useEffect } from "react";
import { AuthContext } from "../../context/AuthContext";
import {
    Table,
    Spin,
    Typography,
    Input,
    Select,
    DatePicker,
    Row,
    Col,
    Modal,
    Space,
    Tag,
    Button,
} from "antd";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as XLSX from "xlsx";
import EmployeeProfile from "../EmployeeProfile";

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

// Map each tab key to the statuses it should fetch
const TAB_STATUSES = {
    Pending: ["Pending Approval"],
    Approved: ["Approved"],
    Rejected: ["Rejected"],
};

const TAB_CONFIG = [
    {
        key: "Pending",
        label: "Pending",
        activeColor: "#faad14",
    },
    {
        key: "Approved",
        label: "Approved",
        activeColor: "#4CAF50",
    },
    {
        key: "Rejected",
        label: "Rejected",
        activeColor: "#f5222d",
    },
];

export default function EmpPermissions() {
    const [permissions, setPermissions] = useState([]);
    const [filteredPermissions, setFilteredPermissions] = useState([]);
    const [loading, setLoading] = useState(false);

    const [selectedId, setSelectedId] = useState(null);
    const [showEmp, setShowEmp] = useState(false);

    const { authData, backUrl } = useContext(AuthContext);

    const [Organisations, setOrganisations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);

    const [pagination, setPagination] = useState({
        current: 1,
        total: 0,
    });

    const onBack = () => setShowEmp(false);

    // Active tab: "Pending" | "Approved" | "Rejected"
    const [activeStatusTab, setActiveStatusTab] = useState("Pending");

    // ── Approve Modal ──
    const [showApproveModal, setShowApproveModal] = useState(false);
    const [approvePermission, setApprovePermission] = useState(null);
    const [approveRemarks, setApproveRemarks] = useState("");
    const [approving, setApproving] = useState(false);

    // ── Reject Modal ──
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectPermission, setRejectPermission] = useState(null);
    const [rejectReason, setRejectReason] = useState("");
    const [rejecting, setRejecting] = useState(false);

    // Bulk Action States
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [showBulkApprove, setShowBulkApprove] = useState(false);
    const [showBulkReject, setShowBulkReject] = useState(false);
    const [bulkRemarks, setBulkRemarks] = useState("");
    const [bulkProcessing, setBulkProcessing] = useState(false);

    const [filters, setFilters] = useState({
        name: "",
        emp_code: "",
        session: "",
        dateRange: [],
        organisation: "",
        department: "",
        designation: "",
    });

    // ---------- Helper accessors ----------
    const getEmployeeFieldId = (employee, fieldBase) => {
        if (!employee) return null;
        if (employee[fieldBase] !== undefined && employee[fieldBase] !== null) return employee[fieldBase];
        const alt = `${fieldBase}_id`;
        if (employee[alt] !== undefined && employee[alt] !== null) return employee[alt];
        return null;
    };

    const getEmployeeFieldName = (employee, nameFieldCandidates = []) => {
        if (!employee) return "";
        for (const k of nameFieldCandidates) {
            if (employee[k]) return employee[k];
        }
        return "";
    };

    // ---------- Core fetch: supports multiple statuses ----------
    const fetchPermissionsByStatuses = async (page = 1, statuses = []) => {
        setLoading(true);
        try {
            const statusParams = statuses
                .map((s) => `approve_status=${encodeURIComponent(s)}`)
                .join("&");

            const res = await axios.get(
                `${backUrl}/api/hr/permissions/?page=${page}&${statusParams}&is_employee_view=false`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            const data = res.data?.results ?? [];
            setPermissions(data);
            setPagination((prev) => ({
                ...prev,
                current: page,
                total: res.data?.count ?? data.length,
            }));
        } catch (error) {
            console.error("Error fetching permissions:", error);
            toast.error("Failed to load permission data");
        } finally {
            setLoading(false);
        }
    };

    // ---------- Refresh with current tab ----------
    const refreshData = () => {
        fetchPermissionsByStatuses(pagination.current, TAB_STATUSES[activeStatusTab]);
    };

    // ---------- Tab click ----------
    const handleTabClick = (tabKey) => {
        setActiveStatusTab(tabKey);
        setSelectedRowKeys([]);
        fetchPermissionsByStatuses(1, TAB_STATUSES[tabKey]);
    };

    const fetchOrganisation = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/organisations/`, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });
            setOrganisations(res.data?.results ?? res.data ?? []);
        } catch (err) {
            console.error(err);
            toast.error("Failed to load organisations");
        }
    };

    const fetchDepartments = async (organisationId) => {
        if (!organisationId) { setDepartments([]); return; }
        try {
            const res = await axios.get(
                `${backUrl}/api/emp/departments/?organisation=${organisationId}`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            setDepartments(res.data?.results ?? res.data ?? []);
        } catch (err) {
            console.error(err);
            toast.error("Failed to load departments");
        }
    };

    const fetchDesignations = async (departmentId) => {
        if (!departmentId) { setDesignations([]); return; }
        try {
            const res = await axios.get(
                `${backUrl}/api/emp/designations/?department=${departmentId}`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            setDesignations(res.data?.results ?? res.data ?? []);
        } catch (err) {
            console.error(err);
            toast.error("Failed to load designations");
        }
    };

    useEffect(() => {
        fetchOrganisation();
        fetchPermissionsByStatuses(1, TAB_STATUSES["Pending"]);
    }, []);

    useEffect(() => {
        if (filters.organisation) {
            fetchDepartments(filters.organisation);
        } else {
            setDepartments([]);
            setDesignations([]);
            setFilters((prev) => ({ ...prev, department: "", designation: "" }));
        }
    }, [filters.organisation]);

    useEffect(() => {
        if (filters.department) {
            fetchDesignations(filters.department);
        } else {
            setDesignations([]);
            setFilters((prev) => ({ ...prev, designation: "" }));
        }
    }, [filters.department]);

    // ---------- Filtering ----------
    useEffect(() => {
        let result = [...permissions];

        if (filters.name) {
            const q = filters.name.toLowerCase();
            result = result.filter((item) =>
                `${item?.employee?.first_name ?? ""} ${item?.employee?.last_name ?? ""}`.toLowerCase().includes(q)
            );
        }

        if (filters.emp_code) {
            const q = filters.emp_code.toLowerCase();
            result = result.filter((item) =>
                (item?.employee?.emp_code ?? "").toLowerCase().includes(q)
            );
        }

        if (filters.session) {
            result = result.filter((item) =>
                (item.session ?? "").toString().toLowerCase() === filters.session.toString().toLowerCase()
            );
        }

        if (filters.dateRange?.length === 2) {
            const [start, end] = filters.dateRange;
            result = result.filter((item) => {
                const itemDate = dayjs(item.date);
                return (
                    itemDate.isAfter(dayjs(start).subtract(1, "day")) &&
                    itemDate.isBefore(dayjs(end).add(1, "day"))
                );
            });
        }

        if (filters.organisation) {
            result = result.filter((item) => {
                const emp = item.employee ?? {};
                const empOrgId = getEmployeeFieldId(emp, "organisation");
                const orgName = getEmployeeFieldName(emp, ["organisation_name", "organisation"]);
                if (/^\d+$/.test(String(filters.organisation))) {
                    return String(empOrgId) === String(filters.organisation);
                }
                return String(orgName).toLowerCase() === String(filters.organisation).toLowerCase();
            });
        }

        if (filters.department) {
            result = result.filter((item) => {
                const emp = item.employee ?? {};
                const empDeptId = getEmployeeFieldId(emp, "department");
                const deptName = getEmployeeFieldName(emp, ["department_name", "department"]);
                if (/^\d+$/.test(String(filters.department))) {
                    return String(empDeptId) === String(filters.department);
                }
                return String(deptName).toLowerCase() === String(filters.department).toLowerCase();
            });
        }

        if (filters.designation) {
            result = result.filter((item) => {
                const emp = item.employee ?? {};
                const empDesigId = getEmployeeFieldId(emp, "designation");
                const desigName = getEmployeeFieldName(emp, ["designation_name", "designation"]);
                if (/^\d+$/.test(String(filters.designation))) {
                    return String(empDesigId) === String(filters.designation);
                }
                return String(desigName).toLowerCase() === String(filters.designation).toLowerCase();
            });
        }

        setFilteredPermissions(result);
    }, [filters, permissions]);

    // ---------- Approve handler ----------
    const handleApprove = async () => {
        if (!approvePermission) return;
        setApproving(true);
        try {
            await axios.post(
                `${backUrl}/api/hr/permissions/approve/`,
                { permission_ids: [approvePermission.id], remarks: approveRemarks },
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            toast.success("Permission approved successfully.");
            setShowApproveModal(false);
            setApprovePermission(null);
            setApproveRemarks("");
            refreshData();
        } catch (error) {
            toast.error(
                error?.response?.data?.error ||
                error?.response?.data?.detail ||
                "Approval failed"
            );
        } finally {
            setApproving(false);
        }
    };

    // ---------- Reject handler ----------
    const handleReject = async () => {
        if (!rejectPermission) return;
        if (!rejectReason.trim()) {
            toast.error("Please provide a reason for rejection.");
            return;
        }
        setRejecting(true);
        try {
            await axios.post(
                `${backUrl}/api/hr/permissions/reject/`,
                { permission_ids: [rejectPermission.id], remarks: rejectReason },
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            toast.success("Permission rejected successfully.");
            setShowRejectModal(false);
            setRejectPermission(null);
            setRejectReason("");
            refreshData();
        } catch (error) {
            toast.error(
                error?.response?.data?.error ||
                error?.response?.data?.detail ||
                "Rejection failed"
            );
        } finally {
            setRejecting(false);
        }
    };

    // ---------- Bulk Approve ----------
    const handleBulkApprove = async () => {
        if (selectedRowKeys.length === 0) { toast.warning("Please select at least one record"); return; }
        setBulkProcessing(true);
        try {
            const res = await axios.post(
                `${backUrl}/api/hr/permissions/approve/`,
                { permission_ids: selectedRowKeys, remarks: bulkRemarks },
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            toast.success(res?.data?.message || res?.data?.detail || "Permissions approved successfully");
            setBulkRemarks("");
            setShowBulkApprove(false);
            setSelectedRowKeys([]);
            refreshData();
        } catch (error) {
            toast.error(error?.response?.data?.error || error?.response?.data?.detail || "Bulk approval failed");
        } finally {
            setBulkProcessing(false);
        }
    };

    // ---------- Bulk Reject ----------
    const handleBulkReject = async () => {
        if (selectedRowKeys.length === 0) { toast.warning("Please select at least one record"); return; }
        if (!bulkRemarks.trim()) { toast.warning("Please provide a reason for rejection"); return; }
        setBulkProcessing(true);
        try {
            const res = await axios.post(
                `${backUrl}/api/hr/permissions/reject/`,
                { permission_ids: selectedRowKeys, rejection_reason: bulkRemarks },
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            toast.success(res?.data?.message || res?.data?.detail || "Permissions rejected successfully");
            setBulkRemarks("");
            setShowBulkReject(false);
            setSelectedRowKeys([]);
            refreshData();
        } catch (error) {
            toast.error(error?.response?.data?.error || error?.response?.data?.detail || "Bulk rejection failed");
        } finally {
            setBulkProcessing(false);
        }
    };

    // ---------- Excel Download ----------
    const downloadExcel = () => {
        if (!filteredPermissions.length) {
            toast.error("No permission records available to download.");
            return;
        }
        const rows = filteredPermissions.map((item) => ({
            "Employee Name": `${item?.employee?.first_name ?? ""} ${item?.employee?.last_name ?? ""}`.trim(),
            "Employee Code": item?.employee?.emp_code ?? "-",
            Designation: getEmployeeFieldName(item.employee, ["designation_name", "designation"]) || getEmployeeFieldId(item.employee, "designation") || "-",
            "Hours Applied": item?.hours_requested ?? "-",
            Time: item?.time ?? "-",
            Session: item?.session ?? "-",
            Date: item.date ? new Date(item.date).toLocaleDateString("en-IN") : "-",
            Reason: item?.reason ?? "-",
            Status: item?.approve_status ?? "-",
            Organisation: getEmployeeFieldName(item.employee, ["organisation_name", "organisation"]) || getEmployeeFieldId(item.employee, "organisation") || "-",
            Department: getEmployeeFieldName(item.employee, ["department_name", "department"]) || getEmployeeFieldId(item.employee, "department") || "-",
        }));
        const worksheet = XLSX.utils.json_to_sheet(rows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Permissions");
        XLSX.writeFile(workbook, `${activeStatusTab}_Permissions.xlsx`);
    };

    // ---------- Table pagination change ----------
    const handleTableChange = (pag) => {
        const newPage = pag.current;
        setPagination((prev) => ({ ...prev, current: newPage }));
        fetchPermissionsByStatuses(newPage, TAB_STATUSES[activeStatusTab]);
    };

    // ---------- Row Selection ----------
    const rowSelection = {
        selectedRowKeys,
        onChange: (selectedKeys) => setSelectedRowKeys(selectedKeys),
        getCheckboxProps: (record) => ({ name: record.id }),
    };

    // ---------- Table Columns ----------
    const columns = [
        {
            title: "Employee Code",
            dataIndex: ["employee", "emp_code"],
            key: "emp_code",
            render: (text, record) => (
                <a
                    onClick={() => { setSelectedId(record.employee.id); setShowEmp(true); }}
                    style={{ cursor: "pointer", color: "#1890ff" }}
                >
                    {text}
                </a>
            ),
            width: 180,
        },
        {
            title: "Employee Name",
            dataIndex: ["employee", "first_name"],
            key: "name",
            fixed: "left",
        },
        { title: "Designation", dataIndex: ["employee", "designation_name"], key: "desg" },
        { title: "Hours Applied", dataIndex: "hours_requested", key: "hours_requested" },
        { title: "Time", dataIndex: "time", key: "time" },
        { title: "Session", dataIndex: "session", key: "session" },
        {
            title: "Date",
            dataIndex: "date",
            key: "date",
            render: (date) => (date ? new Date(date).toLocaleDateString("en-IN") : ""),
        },
        { title: "Reason", dataIndex: "reason", key: "reason" },
        {
            title: "Remarks",
            key: "remarks",
            width: 400,
            render: (_, record) => {
                const approvals = record.approvals || [];
                if (!approvals.length) return "-";
                return (
                    <div style={{ whiteSpace: "normal", wordWrap: "break-word", maxWidth: "400px" }}>
                        {approvals.map((a, index) => (
                            <div
                                key={index}
                                style={{
                                    borderBottom: index < approvals.length - 1 ? "1px solid #ddd" : "none",
                                    paddingBottom: "6px",
                                    marginBottom: "6px",
                                }}
                            >
                                <strong>
                                    {a.approver?.first_name || a.approver?.last_name
                                        ? `${a.approver?.first_name} ${a.approver?.last_name}`
                                        : a.approver?.user?.username}
                                    :
                                </strong>{" "}
                                <span style={{ color: "green" }}>{a.rejection_reason}</span>
                                <span style={{ color: "green" }}>{a.approver_reason}</span>
                                {a.cancellation_reason && (
                                    <div
                                        style={{
                                            marginTop: "4px",
                                            fontWeight: 600,
                                            color: "red",
                                            backgroundColor: "#ffe6e6",
                                            padding: "3px 6px",
                                            borderRadius: "6px",
                                            display: "inline-block",
                                        }}
                                    >
                                        Cancellation Remarks: {a.cancellation_reason}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                );
            },
        },
        {
            title: "Status",
            dataIndex: "approve_status",
            key: "status",
            render: (status) => {
                const color =
                    status === "Approved" || status === "Cancellation Approved"
                        ? "green"
                        : status === "Rejected" || status === "Cancellation Rejected"
                            ? "red"
                            : status === "Cancelled"
                                ? "gray"
                                : "#faad14";
                return <span style={{ color, fontWeight: 600 }}>{status ?? "-"}</span>;
            },
        },
        authData?.employee?.role !== 2 && {
            title: "Action",
            key: "action",
            render: (_, record) => (
                <Space>
                    <Button
                        type="primary"
                        shape="circle"
                        icon={<CheckOutlined />}
                        style={{ backgroundColor: "#4CAF50", borderColor: "#4CAF50" }}
                        onClick={() => {
                            setApprovePermission(record);
                            setApproveRemarks("");
                            setShowApproveModal(true);
                        }}
                    />
                    <Button
                        danger
                        type="primary"
                        shape="circle"
                        icon={<CloseOutlined />}
                        onClick={() => {
                            setRejectPermission(record);
                            setRejectReason("");
                            setShowRejectModal(true);
                        }}
                    />
                </Space>
            ),
        },
        { title: "Organisation", dataIndex: ["employee", "organisation_name"], key: "org" },
        { title: "Department", dataIndex: ["employee", "department_name"], key: "dept" },
    ].filter(Boolean);

    return (
        <div style={{ padding: "20px" }}>
            {showEmp ? (
                <EmployeeProfile empId={selectedId} onBack={onBack} DisableEditOption={true} />
            ) : (
                <>
                    <ToastContainer />

                    <Title level={4} style={{ marginBottom: 16, textTransform: "uppercase", color: "#0e497b" }}>
                        Employee Permission Records
                    </Title>

                    {/* Status Tab Buttons */}
                    <div style={{ textAlign: "center", marginBottom: "20px" }}>
                        {TAB_CONFIG.map((tab) => {
                            const isActive = activeStatusTab === tab.key;
                            return (
                                <Button
                                    key={tab.key}
                                    type={isActive ? "primary" : "default"}
                                    onClick={() => handleTabClick(tab.key)}
                                    title={tab.subLabel}
                                    style={{
                                        marginRight: "10px",
                                        backgroundColor: isActive ? tab.activeColor : undefined,
                                        borderColor: isActive ? tab.activeColor : undefined,
                                        color: isActive ? "#fff" : undefined,
                                    }}
                                >
                                    {tab.label}
                                </Button>
                            );
                        })}
                    </div>

                    {/* Active tab sub-label */}
                    <div style={{ textAlign: "center", marginBottom: "12px", color: "#888", fontSize: "13px" }}>
                        {TAB_CONFIG.find((t) => t.key === activeStatusTab)?.subLabel}
                    </div>

                    {/* Filter Box */}
                    <div style={{ background: "#fff", padding: "15px", borderRadius: "10px", marginBottom: "15px" }}>
                        <Row gutter={[16, 16]}>
                            <Col xs={24} sm={12} md={6}>
                                <Input
                                    placeholder="Employee Name"
                                    value={filters.name}
                                    onChange={(e) => setFilters({ ...filters, name: e.target.value })}
                                />
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                                <Input
                                    placeholder="Employee Code"
                                    value={filters.emp_code}
                                    onChange={(e) => setFilters({ ...filters, emp_code: e.target.value })}
                                />
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                                <Select
                                    placeholder="Session"
                                    value={filters.session || undefined}
                                    onChange={(value) => setFilters({ ...filters, session: value })}
                                    allowClear
                                    style={{ width: "100%" }}
                                >
                                    {[...new Set(permissions.map((p) => p.session).filter(Boolean))].map((type) => (
                                        <Option key={type} value={type}>{type}</Option>
                                    ))}
                                </Select>
                            </Col>
                            <Col xs={24} sm={12} md={8}>
                                <RangePicker
                                    format="DD/MM/YYYY"
                                    value={filters.dateRange}
                                    onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
                                    style={{ width: "100%" }}
                                />
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                                <Select
                                    placeholder="Organisation"
                                    value={filters.organisation || undefined}
                                    onChange={(value) => setFilters({ ...filters, organisation: value, department: "", designation: "" })}
                                    allowClear
                                    style={{ width: "100%" }}
                                >
                                    {Organisations.map((org) => {
                                        const key = org.id ?? org.name;
                                        const label = org.name ?? String(key);
                                        return <Option key={key} value={key}>{label}</Option>;
                                    })}
                                </Select>
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                                <Select
                                    placeholder="Department"
                                    value={filters.department || undefined}
                                    onChange={(value) => setFilters({ ...filters, department: value, designation: "" })}
                                    allowClear
                                    disabled={!filters.organisation}
                                    style={{ width: "100%" }}
                                >
                                    {departments.map((dept) => {
                                        const key = dept.id ?? dept.name;
                                        const label = dept.name ?? String(key);
                                        return <Option key={key} value={key}>{label}</Option>;
                                    })}
                                </Select>
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                                <Select
                                    placeholder="Designation"
                                    value={filters.designation || undefined}
                                    onChange={(value) => setFilters({ ...filters, designation: value })}
                                    allowClear
                                    disabled={!filters.department}
                                    style={{ width: "100%" }}
                                >
                                    {designations.map((des) => {
                                        const key = des.id ?? des.name;
                                        const label = des.name ?? String(key);
                                        return <Option key={key} value={key}>{label}</Option>;
                                    })}
                                </Select>
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                                <Button
                                    type="primary"
                                    style={{ background: "#0e497b", borderColor: "#0e497b", width: "100%" }}
                                    onClick={downloadExcel}
                                >
                                    Download Excel
                                </Button>
                            </Col>
                        </Row>
                    </div>

                    {/* Bulk Action Buttons */}
                    {selectedRowKeys.length > 0 && (
                        <div style={{ marginBottom: 16, textAlign: "center" }}>
                            <Space>
                                <Tag color="blue">{selectedRowKeys.length} selected</Tag>
                                <Button
                                    type="primary"
                                    icon={<CheckOutlined />}
                                    style={{ backgroundColor: "#4CAF50", borderColor: "#4CAF50" }}
                                    onClick={() => setShowBulkApprove(true)}
                                >
                                    Bulk Approve
                                </Button>
                                <Button
                                    danger
                                    type="primary"
                                    icon={<CloseOutlined />}
                                    onClick={() => setShowBulkReject(true)}
                                >
                                    Bulk Reject
                                </Button>
                                <Button onClick={() => setSelectedRowKeys([])}>Clear Selection</Button>
                            </Space>
                        </div>
                    )}

                    {/* Table */}
                    {loading ? (
                        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
                            <Spin size="large" />
                        </div>
                    ) : (
                        <div style={{ overflowX: "auto" }}>
                            <Table
                                columns={columns}
                                dataSource={filteredPermissions}
                                rowKey={(record) => record.id}
                                rowSelection={rowSelection}
                                bordered
                                pagination={{
                                    current: pagination.current,
                                    total: pagination.total,
                                    pageSize: 25,
                                    showSizeChanger: false,
                                }}
                                onChange={handleTableChange}
                                scroll={{ x: "max-content" }}
                                components={{
                                    header: {
                                        cell: (props) => (
                                            <th {...props} style={{ backgroundColor: "#0e497b", color: "white", whiteSpace: "nowrap" }}>
                                                {props.children}
                                            </th>
                                        ),
                                    },
                                }}
                            />
                        </div>
                    )}

                    {/* ── Approve Modal ── */}
                    <Modal
                        title="Approval Remarks"
                        open={showApproveModal}
                        footer={null}
                        onCancel={() => { setShowApproveModal(false); setApprovePermission(null); }}
                        width={600}
                        centered
                    >
                        <p>
                            <strong>Employee:</strong> {approvePermission?.employee?.first_name}{" "}
                            {approvePermission?.employee?.last_name} ({approvePermission?.employee?.emp_code})
                        </p>
                        <Input.TextArea
                            rows={4}
                            placeholder="Enter remarks here (optional)..."
                            value={approveRemarks}
                            onChange={(e) => setApproveRemarks(e.target.value)}
                            style={{ borderRadius: "8px", marginBottom: "16px" }}
                        />
                        <div style={{ textAlign: "right" }}>
                            <Button
                                style={{ marginRight: "8px" }}
                                onClick={() => { setShowApproveModal(false); setApprovePermission(null); }}
                                disabled={approving}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="primary"
                                loading={approving}
                                disabled={approving}
                                style={{ backgroundColor: "#4CAF50", borderColor: "#4CAF50" }}
                                onClick={handleApprove}
                            >
                                {approving ? "Approving..." : "Approve"}
                            </Button>
                        </div>
                    </Modal>

                    {/* ── Reject Modal ── */}
                    <Modal
                        title="Reason for Rejection"
                        open={showRejectModal}
                        footer={null}
                        onCancel={() => { setShowRejectModal(false); setRejectPermission(null); }}
                        width={600}
                        centered
                    >
                        <p>
                            <strong>Employee:</strong> {rejectPermission?.employee?.first_name}{" "}
                            {rejectPermission?.employee?.last_name} ({rejectPermission?.employee?.emp_code})
                        </p>
                        <Input.TextArea
                            rows={4}
                            placeholder="Enter reason here..."
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            style={{
                                borderRadius: "8px",
                                marginBottom: "16px",
                                borderColor: rejectReason.trim() ? "#d9d9d9" : "red",
                            }}
                        />
                        <div style={{ textAlign: "right" }}>
                            <Button
                                style={{ marginRight: "8px" }}
                                onClick={() => { setShowRejectModal(false); setRejectPermission(null); }}
                                disabled={rejecting}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="primary"
                                danger
                                loading={rejecting}
                                disabled={rejecting}
                                onClick={handleReject}
                            >
                                {rejecting ? "Rejecting..." : "Reject"}
                            </Button>
                        </div>
                    </Modal>

                    {/* ── Bulk Approve Modal ── */}
                    <Modal
                        title={`Bulk Approve (${selectedRowKeys.length} permissions)`}
                        open={showBulkApprove}
                        footer={null}
                        onCancel={() => { setShowBulkApprove(false); setBulkRemarks(""); }}
                        width={600}
                        centered
                    >
                        <Input.TextArea
                            rows={4}
                            placeholder="Enter common remarks for all selected permissions (optional)..."
                            value={bulkRemarks}
                            onChange={(e) => setBulkRemarks(e.target.value)}
                            style={{ borderRadius: "8px", marginBottom: "16px" }}
                        />
                        <div style={{ textAlign: "right" }}>
                            <Button
                                style={{ marginRight: "8px" }}
                                onClick={() => { setShowBulkApprove(false); setBulkRemarks(""); }}
                                disabled={bulkProcessing}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="primary"
                                loading={bulkProcessing}
                                disabled={bulkProcessing}
                                style={{ backgroundColor: "#4CAF50", borderColor: "#4CAF50" }}
                                onClick={handleBulkApprove}
                            >
                                {bulkProcessing ? "Approving..." : "Approve All"}
                            </Button>
                        </div>
                    </Modal>

                    {/* ── Bulk Reject Modal ── */}
                    <Modal
                        title={`Bulk Reject (${selectedRowKeys.length} permissions)`}
                        open={showBulkReject}
                        footer={null}
                        onCancel={() => { setShowBulkReject(false); setBulkRemarks(""); }}
                        width={600}
                        centered
                    >
                        <Input.TextArea
                            rows={4}
                            placeholder="Enter common rejection reason for all selected permissions..."
                            value={bulkRemarks}
                            onChange={(e) => setBulkRemarks(e.target.value)}
                            style={{ borderRadius: "8px", marginBottom: "16px" }}
                        />
                        <div style={{ textAlign: "right" }}>
                            <Button
                                style={{ marginRight: "8px" }}
                                onClick={() => { setShowBulkReject(false); setBulkRemarks(""); }}
                                disabled={bulkProcessing}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="primary"
                                danger
                                loading={bulkProcessing}
                                disabled={bulkProcessing || !bulkRemarks.trim()}
                                onClick={handleBulkReject}
                            >
                                {bulkProcessing ? "Rejecting..." : "Reject All"}
                            </Button>
                        </div>
                    </Modal>
                </>
            )}
        </div>
    );
}